import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Compare as CompareIcon,
  PlayArrow as ActivateIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

function PromptVersions() {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: View, 1: Compare
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newVersionData, setNewVersionData] = useState({
    label: '',
    description: '',
    content: '',
    activateImmediately: false
  });
  const [creating, setCreating] = useState(false);
  const [editingFromVersion, setEditingFromVersion] = useState(null);

  // Activation dialog state
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [versionToActivate, setVersionToActivate] = useState(null);
  const [activating, setActivating] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedVersion, setEditedVersion] = useState(null);
  const [saving, setSaving] = useState(false);

  // Compare state
  const [compareVersion1, setCompareVersion1] = useState(null);
  const [compareVersion2, setCompareVersion2] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchVersions();
  }, []);

  // Exit edit mode when changing versions
  useEffect(() => {
    if (editMode) {
      setEditMode(false);
      setEditedVersion(null);
    }
  }, [selectedVersion]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/prompt-versions');
      setVersions(response.data);

      // Auto-select active version
      const active = response.data.find(v => v.isActive);
      if (active) {
        setSelectedVersion(active);
      } else if (response.data.length > 0) {
        setSelectedVersion(response.data[0]);
      }

      setError('');
    } catch (err) {
      console.error('Error fetching versions:', err);
      setError('Failed to load prompt versions');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterEditMode = (version) => {
    setEditedVersion({
      ...version,
      label: version.label || '',
      description: version.description || '',
      content: version.content
    });
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedVersion(null);
  };

  const handleSaveEdit = async () => {
    if (!editedVersion || !editedVersion.content.trim()) {
      showSnackbar('Please enter prompt content', 'error');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/api/prompt-versions/${editedVersion._id}`, {
        content: editedVersion.content,
        label: editedVersion.label,
        description: editedVersion.description
      });

      showSnackbar('Version updated successfully!', 'success');
      setEditMode(false);
      setEditedVersion(null);
      await fetchVersions();
    } catch (err) {
      console.error('Error updating version:', err);
      showSnackbar('Failed to update version', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditVersion = (version) => {
    setEditingFromVersion(version);
    setNewVersionData({
      label: version.label || '',
      description: `Based on Version ${version.version}`,
      content: version.content,
      activateImmediately: true
    });
    setCreateDialogOpen(true);
  };

  const handleCreateVersion = async () => {
    if (!newVersionData.content.trim()) {
      showSnackbar('Please enter prompt content', 'error');
      return;
    }

    try {
      setCreating(true);
      await api.post('/api/prompt-versions', newVersionData);

      showSnackbar(
        newVersionData.activateImmediately
          ? 'Version created and activated successfully!'
          : 'Version created successfully!',
        'success'
      );

      setCreateDialogOpen(false);
      setNewVersionData({
        label: '',
        description: '',
        content: '',
        activateImmediately: false
      });
      setEditingFromVersion(null);

      await fetchVersions();
    } catch (err) {
      console.error('Error creating version:', err);
      showSnackbar('Failed to create version', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateDialog = () => {
    if (!creating) {
      setCreateDialogOpen(false);
      setNewVersionData({
        label: '',
        description: '',
        content: '',
        activateImmediately: false
      });
      setEditingFromVersion(null);
    }
  };

  const handleActivateClick = (version) => {
    setVersionToActivate(version);
    setActivateDialogOpen(true);
  };

  const handleActivateConfirm = async () => {
    if (!versionToActivate) return;

    try {
      setActivating(true);
      await api.put(`/api/prompt-versions/${versionToActivate._id}/activate`);

      showSnackbar(`Version ${versionToActivate.version} activated successfully!`, 'success');
      setActivateDialogOpen(false);
      setVersionToActivate(null);

      await fetchVersions();
    } catch (err) {
      console.error('Error activating version:', err);
      showSnackbar('Failed to activate version', 'error');
    } finally {
      setActivating(false);
    }
  };

  const handleDeleteClick = (version) => {
    setVersionToDelete(version);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!versionToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/api/prompt-versions/${versionToDelete._id}`);

      showSnackbar(`Version ${versionToDelete.version} deleted successfully!`, 'success');
      setDeleteDialogOpen(false);
      setVersionToDelete(null);

      // If deleted version was selected, select another
      if (selectedVersion?._id === versionToDelete._id) {
        setSelectedVersion(null);
      }

      await fetchVersions();
    } catch (err) {
      console.error('Error deleting version:', err);
      showSnackbar(err.response?.data?.error || 'Failed to delete version', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const then = new Date(date);
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  };

  const renderDiff = () => {
    if (!compareVersion1 || !compareVersion2) {
      return (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Select two versions to compare
        </Typography>
      );
    }

    const lines1 = compareVersion1.content.split('\n');
    const lines2 = compareVersion2.content.split('\n');
    const maxLines = Math.max(lines1.length, lines2.length);

    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Version {compareVersion1.version}
            {compareVersion1.label && ` - ${compareVersion1.label}`}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
            <Box component="pre" sx={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {lines1.map((line, idx) => {
                const isDifferent = lines2[idx] !== line;
                return (
                  <Box
                    key={idx}
                    sx={{
                      bgcolor: isDifferent ? 'rgba(211, 47, 47, 0.1)' : 'transparent',
                      px: 1,
                      py: 0.25
                    }}
                  >
                    <Typography component="span" sx={{
                      color: 'text.secondary',
                      mr: 2,
                      userSelect: 'none',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }}>
                      {idx + 1}
                    </Typography>
                    {line || ' '}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Version {compareVersion2.version}
            {compareVersion2.label && ` - ${compareVersion2.label}`}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: '60vh', overflow: 'auto' }}>
            <Box component="pre" sx={{
              margin: 0,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {lines2.map((line, idx) => {
                const isDifferent = lines1[idx] !== line;
                return (
                  <Box
                    key={idx}
                    sx={{
                      bgcolor: isDifferent ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      px: 1,
                      py: 0.25
                    }}
                  >
                    <Typography component="span" sx={{
                      color: 'text.secondary',
                      mr: 2,
                      userSelect: 'none',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }}>
                      {idx + 1}
                    </Typography>
                    {line || ' '}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeVersion = versions.find(v => v.isActive);
  const latestVersion = versions.length > 0 ? versions[0] : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/settings')}
            sx={{ mb: 2 }}
          >
            Back to Settings
          </Button>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Prompt Version Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage different versions of your AI analysis prompt
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Content */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Panel - Version Timeline */}
          <Paper sx={{ flex: '0 0 350px', p: 3, maxHeight: '80vh', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Versions
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingFromVersion(null);
                  setNewVersionData({
                    label: '',
                    description: '',
                    content: '',
                    activateImmediately: false
                  });
                  setCreateDialogOpen(true);
                }}
                size="small"
              >
                New Version
              </Button>
            </Box>

            {versions.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No versions found
              </Typography>
            ) : (
              <Stack spacing={2}>
                {versions.map((version) => (
                  <Card
                    key={version._id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selectedVersion?._id === version._id ? 'action.selected' : 'background.paper',
                      borderColor: selectedVersion?._id === version._id ? 'primary.main' : 'divider',
                      borderWidth: selectedVersion?._id === version._id ? 2 : 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      }
                    }}
                    onClick={() => setSelectedVersion(version)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <Typography variant="h6" fontWeight="bold">
                          v{version.version}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        {version.isActive && (
                          <Chip
                            label="ACTIVE"
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                        {version._id === latestVersion?._id && !version.isActive && (
                          <Chip
                            label="LATEST"
                            size="small"
                            color="info"
                          />
                        )}
                      </Box>

                      {version.label && (
                        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                          {version.label}
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {getRelativeTime(version.createdAt)}
                      </Typography>

                      {version.createdBy && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {version.createdBy.name?.charAt(0)?.toUpperCase() || 'U'}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">
                            {version.createdBy.name}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Right Panel - Version Details */}
          <Paper sx={{ flex: 1, p: 3 }}>
            {!selectedVersion ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Select a version to view details
                </Typography>
              </Box>
            ) : (
              <>
                {/* Tabs */}
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                  <Tab label="View" />
                  <Tab label="Compare" icon={<CompareIcon />} iconPosition="start" />
                </Tabs>

                {/* View Tab */}
                {activeTab === 0 && (
                  <>
                    {editMode && editedVersion ? (
                      /* Edit Mode */
                      <>
                        <Alert severity="info" sx={{ mb: 3 }}>
                          You are editing Version {editedVersion.version}. Changes will be saved to this version directly.
                        </Alert>

                        <Stack spacing={3}>
                          <TextField
                            label="Label (Optional)"
                            value={editedVersion.label}
                            onChange={(e) => setEditedVersion({ ...editedVersion, label: e.target.value })}
                            fullWidth
                          />

                          <TextField
                            label="Description (Optional)"
                            value={editedVersion.description}
                            onChange={(e) => setEditedVersion({ ...editedVersion, description: e.target.value })}
                            multiline
                            rows={2}
                            fullWidth
                          />

                          <TextField
                            label="Prompt Content"
                            value={editedVersion.content}
                            onChange={(e) => setEditedVersion({ ...editedVersion, content: e.target.value })}
                            multiline
                            rows={20}
                            fullWidth
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem'
                              }
                            }}
                          />

                          <Typography variant="body2" color="text.secondary">
                            Character count: {editedVersion.content.length}
                          </Typography>

                          <Stack direction="row" spacing={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveEdit}
                              disabled={saving || !editedVersion.content.trim()}
                            >
                              {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleCancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      </>
                    ) : (
                      /* View Mode */
                      <>
                        {/* Version Info */}
                        <Card variant="outlined" sx={{ mb: 3 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Typography variant="h5" fontWeight="bold">
                                Version {selectedVersion.version}
                              </Typography>
                              {selectedVersion.isActive && (
                                <Chip label="ACTIVE" color="success" icon={<CheckCircleIcon />} />
                              )}
                            </Box>

                            {selectedVersion.label && (
                              <Typography variant="h6" gutterBottom>
                                {selectedVersion.label}
                              </Typography>
                            )}

                            {selectedVersion.description && (
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {selectedVersion.description}
                              </Typography>
                            )}

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Created:
                            </Typography>
                            <Typography variant="body2">
                              {new Date(selectedVersion.createdAt).toLocaleString()}
                            </Typography>
                            {selectedVersion.createdBy && (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  by
                                </Typography>
                                <Chip
                                  size="small"
                                  avatar={<Avatar>{selectedVersion.createdBy.name?.charAt(0)?.toUpperCase()}</Avatar>}
                                  label={selectedVersion.createdBy.name}
                                />
                              </>
                            )}
                          </Box>

                          {selectedVersion.isActive && selectedVersion.activatedAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Activated:
                              </Typography>
                              <Typography variant="body2">
                                {new Date(selectedVersion.activatedAt).toLocaleString()}
                              </Typography>
                              {selectedVersion.activatedBy && (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    by
                                  </Typography>
                                  <Chip
                                    size="small"
                                    avatar={<Avatar>{selectedVersion.activatedBy.name?.charAt(0)?.toUpperCase()}</Avatar>}
                                    label={selectedVersion.activatedBy.name}
                                  />
                                </>
                              )}
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>

                    {/* Prompt Content */}
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Prompt Content
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: '50vh', overflow: 'auto' }}>
                      <Box component="pre" sx={{
                        margin: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {selectedVersion.content}
                      </Box>
                    </Paper>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Character count: {selectedVersion.content.length}
                    </Typography>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => handleEnterEditMode(selectedVersion)}
                          >
                            Edit This Version
                          </Button>

                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditVersion(selectedVersion)}
                          >
                            Edit & Create New
                          </Button>

                          {!selectedVersion.isActive && (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<ActivateIcon />}
                              onClick={() => handleActivateClick(selectedVersion)}
                            >
                              Activate This Version
                            </Button>
                          )}

                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(selectedVersion)}
                            disabled={selectedVersion.isActive}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </>
                    )}
                  </>
                )}

                {/* Compare Tab */}
                {activeTab === 1 && (
                  <>
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Version 1</InputLabel>
                        <Select
                          value={compareVersion1?._id || ''}
                          onChange={(e) => {
                            const version = versions.find(v => v._id === e.target.value);
                            setCompareVersion1(version);
                          }}
                          label="Version 1"
                        >
                          {versions.map(v => (
                            <MenuItem key={v._id} value={v._id}>
                              v{v.version} {v.label && `- ${v.label}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Typography variant="body1" color="text.secondary">
                        vs
                      </Typography>

                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Version 2</InputLabel>
                        <Select
                          value={compareVersion2?._id || ''}
                          onChange={(e) => {
                            const version = versions.find(v => v._id === e.target.value);
                            setCompareVersion2(version);
                          }}
                          label="Version 2"
                        >
                          {versions.map(v => (
                            <MenuItem key={v._id} value={v._id}>
                              v{v.version} {v.label && `- ${v.label}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {compareVersion1 && compareVersion2 && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="caption">
                          <strong>Legend:</strong> Lines with red background are from Version {compareVersion1.version}.
                          Lines with green background are from Version {compareVersion2.version}.
                        </Typography>
                      </Alert>
                    )}

                    {renderDiff()}
                  </>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Create Version Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCloseCreateDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingFromVersion
              ? `Edit Version ${editingFromVersion.version} (Creates New Version)`
              : 'Create New Prompt Version'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Label (Optional)"
                placeholder="e.g., Enhanced word analysis"
                value={newVersionData.label}
                onChange={(e) => setNewVersionData({ ...newVersionData, label: e.target.value })}
                fullWidth
              />

              <TextField
                label="Description (Optional)"
                placeholder="Brief description of changes..."
                value={newVersionData.description}
                onChange={(e) => setNewVersionData({ ...newVersionData, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />

              <TextField
                label="Prompt Content"
                placeholder="Enter the prompt content..."
                value={newVersionData.content}
                onChange={(e) => setNewVersionData({ ...newVersionData, content: e.target.value })}
                multiline
                rows={15}
                fullWidth
                required
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={newVersionData.activateImmediately}
                    onChange={(e) => setNewVersionData({
                      ...newVersionData,
                      activateImmediately: e.target.checked
                    })}
                  />
                }
                label="Activate immediately after creation"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={creating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateVersion}
              variant="contained"
              disabled={creating || !newVersionData.content.trim()}
            >
              {creating ? <CircularProgress size={24} /> : 'Create Version'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activation Confirmation Dialog */}
        <Dialog open={activateDialogOpen} onClose={() => !activating && setActivateDialogOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Activate Version?
          </DialogTitle>
          <DialogContent>
            <Typography paragraph>
              You are about to activate <strong>Version {versionToActivate?.version}</strong>
              {versionToActivate?.label && ` (${versionToActivate.label})`}.
            </Typography>
            <Typography paragraph>
              This will:
            </Typography>
            <ul>
              {activeVersion && (
                <li>
                  <Typography>
                    Deactivate current version (v{activeVersion.version})
                  </Typography>
                </li>
              )}
              <li>
                <Typography>
                  Use this prompt for all new AI analyses
                </Typography>
              </li>
            </ul>

            {versionToActivate && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Version Preview:
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="caption" component="pre" sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace'
                  }}>
                    {versionToActivate.content.substring(0, 500)}
                    {versionToActivate.content.length > 500 && '...'}
                  </Typography>
                </Paper>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateDialogOpen(false)} disabled={activating}>
              Cancel
            </Button>
            <Button
              onClick={handleActivateConfirm}
              variant="contained"
              color="success"
              disabled={activating}
            >
              {activating ? <CircularProgress size={24} /> : 'Activate Version'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete Version?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>Version {versionToDelete?.version}</strong>
              {versionToDelete?.label && ` (${versionToDelete.label})`}?
            </Typography>
            <Typography sx={{ mt: 2 }} color="error">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}

export default PromptVersions;
